import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNodeStore } from '@/stores/nodeStore';
import type { NodeState } from '@/stores/nodeStore';
import { NodeDataByType, NodeType } from '@/types';
import { readScript, ScriptShotGenRef } from '@/types/scriptNode';

export interface InputAsset {
  id: string;
  type: 'image' | 'video' | 'audio';
  src: string;
  /** 输入角色：product=产品参考图 / style-reference=竞品风格参考图（角标视觉区分；缺省为普通输入） */
  role?: 'product' | 'style-reference';
}

// P4: 自定义比较函数，比较 assets 数组内容而非引用
// 之前 useShallow 订阅整个 nodes 数组，拖拽停止时 updateNodePositions 改变被拖拽节点引用
// 导致 useShallow 判定数组变化，触发所有 GenerateNode 重渲染
// 现在只比较 inputIds 对应节点的 image/videoUri/audioUri，位置变化不触发重渲染
function assetsEqual(prev: InputAsset[], next: InputAsset[]): boolean {
  if (prev.length !== next.length) return false;
  return prev.every((p, i) => p.id === next[i].id && p.src === next[i].src && p.type === next[i].type && p.role === next[i].role);
}

// 类似 useShallow 的模式：用 ref 缓存上一次结果，自定义比较函数决定是否更新
function useCustomEqual<U>(selector: (state: NodeState) => U, equal: (prev: U, next: U) => boolean): (state: NodeState) => U {
  const prev = useRef<U | undefined>(undefined);
  return useCallback((state: NodeState) => {
    const next = selector(state);
    if (prev.current !== undefined && equal(prev.current, next)) {
      return prev.current;
    }
    prev.current = next;
    return next;
  }, [selector, equal]);
}

// v1.4.88 scriptShotRef：分镜生图节点的锚定引用（上游脚本节点 + 资产 id 顺序表），
// 存在时把上游脚本节点的锚定资产图展开为参考图预览；普通节点不传，行为不变
export function useInputAssets(inputIds: string[], scriptShotRef?: ScriptShotGenRef | null) {
  const selector = useCallback((state: NodeState) => {
    const assets: InputAsset[] = [];
    const seen = new Set<string>();
    for (const inputId of inputIds) {
      if (seen.has(inputId)) continue;
      seen.add(inputId);
      const n = state.nodeMap.get(inputId);
      if (!n) continue;
      if (n.data.image) assets.push({ id: n.id, type: 'image', src: n.data.image });
      else if (n.type === NodeType.LAYER_EDIT) {
        // 图层编辑节点：读取手动保存/关闭时合成的输出资产作为输入预览
        const outputImageUrl = (n.data as NodeDataByType<NodeType.LAYER_EDIT>).outputImageUrl;
        if (outputImageUrl) assets.push({ id: n.id, type: 'image', src: outputImageUrl });
      }
      else if (n.type === NodeType.DETAIL_PAGE) {
        // 详情页节点：展开产品图（面板管理 + 手动连线接入）作为输入预览；
        // 开启竞品风格参考出图时同口径展开竞品图（带「风格参考」角标，数量上限与请求侧
        // collectDetailPageStyleReferences 一致：≤2 张、产品图 ≥3 张时压至 1 张）
        const planner = (n.data as NodeDataByType<NodeType.DETAIL_PAGE>).planner;
        const productIds = [...(planner?.productImageNodeIds ?? []), ...(planner?.connectedProductImageNodeIds ?? [])];
        for (const pid of productIds) {
          if (seen.has(pid)) continue;
          seen.add(pid);
          const pn = state.nodeMap.get(pid);
          if (pn?.data.image) assets.push({ id: pid, type: 'image', src: pn.data.image, role: 'product' });
        }
        if (planner?.useCompetitorStyleReference && !planner.skipCompetitor) {
          const maxRefs = productIds.length >= 3 ? 1 : 2;
          if (planner.competitorScreens && planner.competitorScreens.length > 0) {
            // P2.5 切屏模式：预览展示屏切块池（带「风格参考」角标，数量上限与请求侧一致）；
            // 逐区块语义选屏在请求侧按目标节点身份决定，预览无法感知目标区块故展示池整体
            for (const screen of planner.competitorScreens.slice(0, maxRefs)) {
              assets.push({ id: `${n.id}#screen-${screen.index}`, type: 'image', src: screen.url, role: 'style-reference' });
            }
          } else {
            const competitorIds = [...(planner.competitorImageNodeIds ?? []), ...(planner.connectedCompetitorImageNodeIds ?? [])];
            let refCount = 0;
            for (const cid of competitorIds) {
              if (refCount >= maxRefs) break;
              if (seen.has(cid)) continue;
              seen.add(cid);
              const cn = state.nodeMap.get(cid);
              if (cn?.data.image) {
                assets.push({ id: cid, type: 'image', src: cn.data.image, role: 'style-reference' });
                refCount += 1;
              }
            }
          }
        }
      }
      else if (n.type === NodeType.SCRIPT_NODE) {
        // v1.4.88 分镜生图锚定：脚本节点本身无图，按当前节点 scriptShotRef 的 assetIds
        // 顺序展开脚本资产图为参考图预览——与请求侧 collectShotAnchorImages 同源同序
        // （v1.4.98 提示词锚定区块改「@资产名」名字式，芯片图源经名字表解析，
        // 不再依赖本序列的位置编号），资产图重生成后预览自动跟随
        if (scriptShotRef && scriptShotRef.scriptNodeId === n.id && scriptShotRef.assetIds.length > 0) {
          const script = readScript((n.data as { script?: unknown }).script);
          if (script) {
            const anchorSeen = new Set<string>();
            for (const assetId of scriptShotRef.assetIds) {
              if (anchorSeen.has(assetId)) continue;
              anchorSeen.add(assetId);
              const asset = script.assets.find((a) => a.id === assetId);
              if (asset?.imageUrl) {
                assets.push({ id: `${n.id}#asset-${asset.id}`, type: 'image', src: asset.imageUrl });
              }
            }
          }
        }
      }
      else if (n.data.videoUri) assets.push({ id: n.id, type: 'video', src: n.data.videoUri });
      else if (n.data.audioUri) assets.push({ id: n.id, type: 'audio', src: n.data.audioUri });
    }
    return assets;
  }, [inputIds, scriptShotRef]);

  const stableSelector = useCustomEqual(selector, assetsEqual);
  const inputAssets = useNodeStore(stableSelector);

  return inputAssets;
}

/** 智能剪辑连接素材态：首个上游视频（封面源 + 真实时长） */
export interface UpstreamVideo {
  videoUri: string;
  durationSec?: number;
}

function upstreamVideoEqual(prev: UpstreamVideo | null, next: UpstreamVideo | null): boolean {
  if (prev === next) return true;
  if (!prev || !next) return false;
  return prev.videoUri === next.videoUri && prev.durationSec === next.durationSec;
}

// v0.33 连接素材态：读取首个上游视频节点的 videoUri + videoMetadata.duration
// （同 useInputAssets 的自定义相等模式：位置变化/无关更新不触发重渲染）。
// 元数据缺时长时用 video 元素量测兜底（不写回节点，仅展示用）。
export function useUpstreamVideo(inputIds: string[]): UpstreamVideo | null {
  const selector = useCallback((state: NodeState) => {
    for (const inputId of inputIds) {
      const n = state.nodeMap.get(inputId);
      if (!n) continue;
      // 与 useInputAssets 同判定：data.videoUri 存在即上游视频节点
      if (n.data.videoUri) {
        const meta = (n.data as { videoMetadata?: { duration?: number } }).videoMetadata;
        return { videoUri: n.data.videoUri, durationSec: meta?.duration };
      }
    }
    return null;
  }, [inputIds]);
  const stableSelector = useCustomEqual(selector, upstreamVideoEqual);
  const base = useNodeStore(stableSelector);

  const [measured, setMeasured] = useState<number | undefined>(undefined);
  const uri = base?.videoUri;
  const metaDuration = base?.durationSec;
  useEffect(() => {
    setMeasured(undefined);
    if (!uri || (typeof metaDuration === 'number' && metaDuration > 0)) return;
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      if (Number.isFinite(v.duration) && v.duration > 0) setMeasured(v.duration);
    };
    v.src = uri;
    return () => {
      v.onloadedmetadata = null;
    };
  }, [uri, metaDuration]);

  // useMemo 固定对象引用：避免 SmartEditContent / SmartEditQuickInput 的 memo 失效
  return useMemo(
    () => (base ? { videoUri: base.videoUri, durationSec: base.durationSec ?? measured } : null),
    [base, measured],
  );
}
